import { Component, OnInit, Input, signal, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { ClienteService } from '../../../services/cliente/cliente.service';
import { Cliente } from '../../../domain/cliente.domain';
import { Subject, takeUntil } from 'rxjs';
import { HttpParams } from '@angular/common/http';
import { AnuncianteLinkDialogComponent } from '../anunciante-link-dialog/anunciante-link-dialog.component';

@Component({
  selector: 'app-cliente-anunciantes-panel',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    CardModule,
    TagModule,
    AnuncianteLinkDialogComponent
  ],
  templateUrl: './cliente-anunciantes-panel.component.html',
  styleUrls: ['./cliente-anunciantes-panel.component.scss']
})
export class ClienteAnunciantesPanelComponent implements OnInit, OnDestroy {
  @Input() cliente: Cliente | null = null;

  private clienteService = inject(ClienteService);
  private router = inject(Router);

  anunciantes = signal<Cliente[]>([]);
  totalAnunciantes = signal<number>(0);
  loading = signal<boolean>(false);
  loadingMore = signal<boolean>(false);
  showLinkDialog = signal<boolean>(false);
  hasMore = signal<boolean>(true);
  currentPage = signal<number>(1);
  pageSize = 10;

  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    if (this.cliente && this.isAgenciaOrProductor()) {
      this.loadAnunciantes();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  isAgenciaOrProductor(): boolean {
    if (!this.cliente?.tipoCliente) {
      return false;
    }
    return this.cliente.tipoCliente.includes('agencia') || 
           this.cliente.tipoCliente.includes('productor');
  }

  isAnunciante(): boolean {
    if (!this.cliente?.tipoCliente) {
      return false;
    }
    return this.cliente.tipoCliente.includes('anunciante');
  }

  loadAnunciantes(reset: boolean = true): void {
    if (!this.cliente?.id) {
      return;
    }

    if (reset) {
      this.loading.set(true);
      this.currentPage.set(1);
      this.anunciantes.set([]);
      this.hasMore.set(true);
    } else {
      this.loadingMore.set(true);
    }

    const params = new HttpParams()
      .set('tipoCliente', 'anunciante')
      .set('agenciaProductor', this.cliente.id.toString())
      .set('size', this.pageSize.toString())
      .set('page', this.currentPage().toString())
      .set('_order[razonSocial]', 'asc');

    this.clienteService.searchClientesCollection(params).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        const items = response.members;
        const totalItems = response.totalItems;
        const currentItems = this.anunciantes();

        if (reset) {
          this.anunciantes.set(items);
        } else {
          this.anunciantes.set([...currentItems, ...items]);
        }

        this.totalAnunciantes.set(totalItems);
        this.hasMore.set(this.anunciantes().length < totalItems);
        this.loading.set(false);
        this.loadingMore.set(false);
      },
      error: (error) => {
        console.error('Error al cargar anunciantes:', error);
        this.loading.set(false);
        this.loadingMore.set(false);
      }
    });
  }

  loadMoreAnunciantes(): void {
    if (this.loadingMore() || !this.hasMore() || !this.cliente?.id) {
      return;
    }

    this.currentPage.update(page => page + 1);
    this.loadAnunciantes(false);
  }

  onScrollList(event: Event): void {
    const element = event.target as HTMLElement;
    const scrollTop = element.scrollTop;
    const scrollHeight = element.scrollHeight;
    const clientHeight = element.clientHeight;
    
    // Cargar más cuando el usuario esté cerca del final (80% del scroll)
    if (scrollTop + clientHeight >= scrollHeight * 0.8) {
      if (this.hasMore() && !this.loadingMore() && !this.loading()) {
        this.loadMoreAnunciantes();
      }
    }
  }

  openLinkDialog(): void {
    this.showLinkDialog.set(true);
  }

  onAnuncianteLinked(): void {
    this.showLinkDialog.set(false);
    this.loadAnunciantes();
  }

  goToAnunciantes(): void {
    if (this.cliente?.id) {
      this.router.navigate(['/clientes', this.cliente.id, 'anunciantes']);
    }
  }

  getAgenciaProductorName(): string {
    if (!this.cliente?.agenciaProductor) {
      return '';
    }

    if (typeof this.cliente.agenciaProductor === 'string') {
      return 'Agencia/Productor';
    }

    if (typeof this.cliente.agenciaProductor === 'object') {
      return this.cliente.agenciaProductor.razonSocial || 'Agencia/Productor';
    }

    return '';
  }

  goToAgenciaProductor(): void {
    if (!this.cliente?.agenciaProductor) {
      return;
    }

    let id: string | number | null = null;

    if (typeof this.cliente.agenciaProductor === 'string') {
      const match = this.cliente.agenciaProductor.match(/\/(\d+)$/);
      if (match) {
        id = match[1];
      }
    } else if (typeof this.cliente.agenciaProductor === 'object' && this.cliente.agenciaProductor.id) {
      id = this.cliente.agenciaProductor.id;
    }

    if (id) {
      this.router.navigate(['/clientes/edit', id]);
    }
  }

  getClienteId(): string {
    return this.cliente?.id?.toString() || '';
  }
}

